// Approve — the week, one row per worker. GET /api/dispatch/approve?week=.
// A row with a sheet opens it; a row without one offers to build it from
// the clock (POST /api/timesheets/from-clock) and then opens the result.
//
// ── What the endpoint actually returns ──────────────────────────────────────
//
// server/routes/dispatch.js:227 answers
//
//   { start_date, end_date, counts, range,
//     workers: [{ user_id, name, initials, clock_hours, on_clock_now,
//                 timesheet: { id, status, total_hours, submitted_at,
//                              approved_at, adjusted_count } | null,
//                 days: [{ date, clock_hours, sheet_hours }] × 7 }] }
//
// `days` (built at dispatch.js:215-216) and `on_clock_now` (dispatch.js:217)
// were both computed on every request and thrown away by this screen. They
// are the two facts a dispatcher needs and could not see:
//
//   - WHICH DAYS the week's hours fell on. A 41.25 that is five eights is a
//     normal week; a 41.25 that is two twenty-hour days is not, and both read
//     identically as one number.
//   - that somebody's clock is STILL RUNNING. Approving a sheet under an open
//     time entry banks only the hours logged so far, and the rest of that
//     shift never reaches payroll.
//
// ── The week/badge contradiction ────────────────────────────────────────────
//
// This screen is ONE week (dispatch.js:187-189 resolves ?week= to a single
// start-of-week). The nav badge beside "Approve" counts submitted sheets
// across EVERY week — apps/shell/badges.js:32-38 reads
// GET /timesheets?status=submitted, which has no week filter
// (server/routes/timesheets.js:302-331), and takes its `.length`.
//
// So on a Monday the badge says 3 and the screen shows nothing, and the app
// reads as broken. The screen owns half of that contradiction, so it reads the
// same list the badge counts and says out loud where the rest are.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PersonRow, Pill, Stat, SectionLabel, Sheet, Notice, Skeleton, FwButton, formatHours, initialsOf } from '../../../ui/fw';
import { get, post, describeError } from '../../shared/http';
import { addDaysISO } from '../../../utils/scheduleDates';
import { usePageChrome } from '../../shell/chrome';
import { dayLong, dayShort, weekOfLabel, sheetPill, hoursMeta, serverSentence } from '../lib/format';
import './Approve.css';

const list = (a) => (Array.isArray(a) ? a : []);

// An en dash, not an empty cell: a day with nothing on it has to look like an
// answer rather than like a rail that failed to render.
const NO_HOURS = '–';

/** 'Mon' — the rail's column head. `dayShort` gives 'Mon 7'; seven of those do
 *  not fit across a 320px phone, and the week's dates are already in the
 *  header above the list. The full date stays on the cell as a title. */
const dow = (iso) => dayShort(iso).split(' ')[0];

/**
 * '8', '8.25', '12.75' — a day's figure for the rail.
 *
 * Exact, never rounded: `formatHours` two-decimal output with only the padding
 * removed, so 8.25 stays 8.25 and 12.75 stays 12.75. These are hours that
 * become pay and a rounded one on an approval screen would be a plausible
 * wrong number, which CLAUDE.md calls the worst outcome available here.
 *
 * The padding goes because the rail is seven cells ACROSS, not a column. At
 * 320px a cell is 38px (docs/APP_REBUILD.md floor; the arithmetic is in
 * Approve.css) and "12.75" measured 41px against a fallback face — clipped,
 * silently, into a different number. `formatHours`' two decimals exist so a
 * vertical column lines up under .fw-num, which is not what this is.
 */
const railHours = (h) => formatHours(h).replace(/\.?0+$/, '');

/** 'Ana', 'Ana and Ben', 'Ana, Ben and Cara'. */
function nameList(names) {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The day breakdown the API already sends.
 *
 * Which figure: the SHEET hours when there is a sheet — that is what is being
 * approved — and the clock hours when there is not. The caption says which,
 * because "8.00" means two different things in those two cases and a
 * dispatcher cannot tell them apart from the number.
 */
function DayRail({ worker }) {
  const days = list(worker.days);
  if (days.length === 0) return null;
  const fromSheet = !!worker.timesheet;
  return (
    <div className="dp-drail" data-user={worker.user_id}>
      <div className="dp-drail__cap">{fromSheet ? 'Sheet hours by day' : 'Clock hours by day'}</div>
      <div className="dp-drail__days">
        {days.map((d) => {
          const hours = fromSheet ? d.sheet_hours : d.clock_hours;
          const empty = !(Number(hours) > 0);
          return (
            <div
              key={d.date}
              className="dp-drail__day"
              data-date={d.date}
              data-empty={String(empty)}
              title={dayLong(d.date)}
            >
              <span className="dp-drail__dow">{dow(d.date)}</span>
              <span className="dp-drail__hrs fw-num">{empty ? NO_HOURS : railHours(hours)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Approve() {
  const [week, setWeek] = useState(null);
  const [building, setBuilding] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['dispatch', 'approve', week],
    queryFn: () => get(`/dispatch/approve${week ? `?week=${week}` : ''}`),
  });

  // The list the nav badge counts, read here so the two numbers agree by
  // construction rather than by hope.
  //
  // A key of its OWN, deliberately not the badge's ['badges',
  // 'pending-timesheets']: badges.js disables that query for roles it does not
  // consider approvers, and filling its cache entry from here would make a
  // badge appear in the nav that the nav decided not to show. This screen is
  // allowed to explain the badge; it is not allowed to change it.
  //
  // One fetch on mount, no interval — the rate-limiter arithmetic in
  // badges.js:47-64 is about polling, and this does not poll.
  const pending = useQuery({
    queryKey: ['dispatch', 'approve', 'submitted-every-week'],
    queryFn: () => get('/timesheets?status=submitted'),
    retry: false,
    staleTime: 60_000,
  });

  const data = q.data;
  const start = data && data.start_date;
  // An EMPTY title, so AppShell's documented fallback supplies the one name
  // there is — the destination's label in navigation.js, which is "Approvals".
  //
  // This said 'Approve' until 2026-09-10 and the menu row that got you here
  // said "Approvals": a person taps one word and lands on a screen called
  // another. Board and Now were corrected the same way that morning and this
  // one was left, because it was being worked concurrently (#628). Retyping
  // the right string here would diverge again at the next rename; the menu is
  // the one place the name lives.
  //
  // The EYEBROW is untouched, and it is the half the menu could never know:
  // which week's approvals these are.
  usePageChrome('', start ? weekOfLabel(start) : 'This week');
  const counts = (data && data.counts) || {};
  const workers = list(data && data.workers);

  const onClock = workers.filter((w) => w.on_clock_now);

  /**
   * Where the rest of the badge's count lives.
   *
   * `weeks` are the other start-of-weeks with a submitted sheet, newest first.
   * `hiddenHere` is submitted sheets dated to THIS week that this screen can
   * never list: dispatch.js:192-194 selects `is_active = 1 AND role NOT IN
   * ('admin','accounting')`, so a sheet from a deactivated or office account
   * is counted by the badge and invisible in every week. It is clamped at 0,
   * which is what keeps it silent for a caller whose /timesheets answer is
   * narrower than the dispatch one (timesheets.js:318-320 scopes a non-admin,
   * non-manager to their own rows).
   */
  const elsewhere = useMemo(() => {
    const rows = list(pending.data);
    if (!start || rows.length === 0) return null;
    const byWeek = new Map();
    let here = 0;
    for (const t of rows) {
      const wk = String((t && t.start_date) || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(wk)) continue;
      if (wk === start) { here += 1; continue; }
      byWeek.set(wk, (byWeek.get(wk) || 0) + 1);
    }
    const weeks = Array.from(byWeek.entries())
      .map(([wk, count]) => ({ week: wk, count }))
      .sort((a, b) => (a.week < b.week ? 1 : -1));
    const total = weeks.reduce((n, w) => n + w.count, 0);
    const hiddenHere = Math.max(0, here - (Number(counts.to_approve) || 0));
    if (total === 0 && hiddenHere === 0) return null;
    return { weeks, total, hiddenHere };
  }, [pending.data, start, counts.to_approve]);

  const shift = (n) => { if (start) setWeek(addDaysISO(start, n * 7)); };

  const open = (w) => {
    if (w.timesheet && w.timesheet.id) navigate(`/approve/${w.timesheet.id}`);
    else { setError(null); setBuilding(w); }
  };

  const build = async () => {
    if (!building || !start) return;
    setBusy(true);
    setError(null);
    try {
      const sheet = await post('/timesheets/from-clock', { user_id: building.user_id, week_start: start });
      qc.invalidateQueries({ queryKey: ['dispatch', 'approve'] });
      setBuilding(null);
      setBusy(false);
      navigate(`/approve/${sheet.id}`);
    } catch (e) {
      setError(serverSentence(e));
      setBusy(false);
    }
  };

  return (
    <>
      <div className="dp-nav">
        <FwButton variant="ghost" icon="chevronLeft" aria-label="Previous week" onClick={() => shift(-1)} disabled={!start} />
        <div className="dp-nav__label">{start ? `${dayLong(start)} – ${dayLong(data.end_date)}` : ''}</div>
        <FwButton variant="ghost" icon="chevronRight" aria-label="Next week" onClick={() => shift(1)} disabled={!start} />
      </div>
      {q.isPending ? <Skeleton lines={6} /> : null}
      {q.error ? <Notice tone="stop" className="dp-notice">{describeError(q.error)}</Notice> : null}
      {data ? (
        <>
          {onClock.length ? (
            <Notice
              tone="warn"
              className="dp-onclock"
              title={onClock.length === 1 ? '1 worker is still on the clock' : `${onClock.length} workers are still on the clock`}
            >
              {nameList(onClock.map((w) => w.name))}
              {onClock.length === 1 ? ' has an open clock entry' : ' have open clock entries'}
              {' right now, so their hours for this week are still counting up. '}
              Approve after they clock out — a sheet approved under an open entry stops at the hours banked so
              far, and the rest of that shift never reaches payroll.
            </Notice>
          ) : null}

          {elsewhere ? (
            <Notice
              tone="info"
              className="dp-elsewhere"
              title={
                elsewhere.total === 0
                  ? 'The Approve badge is counting more than this week'
                  : elsewhere.total === 1
                    ? '1 more submitted sheet is in another week'
                    : `${elsewhere.total} more submitted sheets are in other weeks`
              }
            >
              <span className="dp-elsewhere__why">The Approve badge counts every week; this screen shows one.</span>
              {elsewhere.weeks.length ? (
                <span className="dp-elsewhere__weeks">
                  {elsewhere.weeks.map((w) => (
                    <FwButton
                      key={w.week}
                      variant="ghost"
                      className="dp-elsewhere__week"
                      onClick={() => setWeek(w.week)}
                    >
                      {`${weekOfLabel(w.week)} · ${w.count}`}
                    </FwButton>
                  ))}
                </span>
              ) : null}
              {elsewhere.hiddenHere ? (
                <span className="dp-elsewhere__hidden">
                  {elsewhere.hiddenHere === 1
                    ? '1 submitted sheet for this week is not in the list above'
                    : `${elsewhere.hiddenHere} submitted sheets for this week are not in the list above`}
                  {' — the list covers active crew only, so a sheet from a deactivated or office account never appears on this screen.'}
                </span>
              ) : null}
            </Notice>
          ) : null}

          <div className="dp-stats">
            <Stat label="To approve" value={counts.to_approve || 0} tone={counts.to_approve ? 'now' : 'neutral'} />
            <Stat label="No sheet" value={counts.no_sheet || 0} tone={counts.no_sheet ? 'warn' : 'neutral'} />
            <Stat label="Approved" value={counts.approved || 0} tone="ok" />
          </div>
          <SectionLabel>Workers</SectionLabel>
          <div className="dp-list" data-section="workers">
            {workers.length === 0 ? <div className="dp-quiet">No active workers.</div> : null}
            {workers.map((w) => {
              const pill = sheetPill(w.timesheet);
              const adjusted = w.timesheet ? w.timesheet.adjusted_count || 0 : 0;
              return (
                <div className="dp-worker" key={w.user_id}>
                  <PersonRow
                    tone={pill.tone}
                    initials={w.initials || initialsOf(w.name)}
                    name={w.name}
                    meta={hoursMeta(w.clock_hours, w.timesheet)}
                    onClick={() => open(w)}
                    data-user-id={w.user_id}
                    data-status={w.timesheet ? w.timesheet.status : 'none'}
                    trailing={
                      <span className="dp-trail">
                        <Pill tone={pill.tone}>{pill.label}</Pill>
                        {w.on_clock_now ? <Pill tone="now" className="dp-onclock-pill">On the clock</Pill> : null}
                        {adjusted > 0 ? <span className="dp-adjusted">{adjusted} adjusted</span> : null}
                      </span>
                    }
                  />
                  <DayRail worker={w} />
                </div>
              );
            })}
          </div>
        </>
      ) : null}

      <Sheet
        open={!!building}
        title={building ? building.name : ''}
        onClose={() => setBuilding(null)}
        footer={<FwButton full size="lg" loading={busy} onClick={build}>Build from clock</FwButton>}
      >
        {building ? (
          <div className="dp-sheet-body">
            {error ? <Notice tone="stop">{error}</Notice> : null}
            <p>
              No timesheet for {weekOfLabel(start).toLowerCase()} yet. The clock has{' '}
              <span className="fw-num">{formatHours(building.clock_hours)}</span> hours for {building.name}.
              Building the sheet from the clock adds one line per job per day, and opens it here.
            </p>
          </div>
        ) : null}
      </Sheet>
    </>
  );
}
